import { motion } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";
import { researchPapers } from "../data/research-bilingual";

export default function Research() {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const researchMetrics = [
    { metric: "2", label: t('research.metrics.publications'), description: t('research.metrics.publicationsDesc') },
    { metric: "0.57", label: t('research.metrics.naiAuc'), description: t('research.metrics.naiAucDesc') },
    { metric: "74.0%", label: t('research.metrics.tsceptionAcc'), description: t('research.metrics.tsceptionAccDesc') },
    { metric: "15+", label: t('research.metrics.techProjects'), description: t('research.metrics.techProjectsDesc') }
  ];

  const publishedPapers = researchPapers.filter(p => p.category === 'published');
  const manuscriptPapers = researchPapers.filter(p => p.category === 'manuscript');
  const articlePapers = researchPapers.filter(p => p.category === 'article');

  const researchAreas = [
    {
      icon: "🧠",
      title: language === 'ja' ? "ブレイン・コンピュータ・インターフェース" : "Brain-Computer Interfaces",
      description: language === 'ja' ? "EEG信号処理、P300分類、運動イメージ、認知状態モニタリング" : "EEG signal processing, P300 classification, Motor Imagery, cognitive state monitoring",
      projects: ["TSception MI-BCI", "NeuroAdaptive Interface (NAI)", "NeuroSync"],
      color: "from-blue-500/10 to-cyan-500/10"
    },
    {
      icon: "🌍",
      title: language === 'ja' ? "環境AI" : "Environmental AI",
      description: language === 'ja' ? "サステナビリティ技術、生物多様性モニタリング、環境影響分析" : "Sustainability tech, biodiversity monitoring, eco-impact analysis",
      projects: ["EcoFusionAI", "EcoPulse α"],
      color: "from-green-500/10 to-emerald-500/10"
    },
    {
      icon: "🤖",
      title: language === 'ja' ? "応用機械学習" : "Applied Machine Learning",
      description: language === 'ja' ? "ヘルスケアAI、感情分析、インテリジェントシステム" : "Healthcare AI, sentiment analysis, intelligent systems",
      projects: ["MoodLift", "SkimSage"],
      color: "from-purple-500/10 to-pink-500/10"
    },
    {
      icon: "💡",
      title: language === 'ja' ? "AIエージェント開発" : "AI Agent Development",
      description: language === 'ja' ? "自律システム、キャリアガイダンス、教育AI、科学的発見" : "Autonomous systems, career guidance, educational AI, scientific discovery",
      projects: ["Neurogenesis AI Scientist", "Decision Provenance Engine", "Udaan-AI"],
      color: "from-orange-500/10 to-red-500/10"
    }
  ];

  const renderPaper = (paper: typeof researchPapers[0], borderColor: string) => {
    const content = language === 'ja' ? paper.ja : paper.en;
    
    return (
      <motion.div
        key={paper.id}
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className={`card card-hover border-l-4 ${borderColor} bg-gradient-to-r from-${borderColor.split('-')[1]}-500/5 to-transparent`}
      >
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h4 className="text-lg font-bold text-white flex-1">{content.title}</h4>
          <span className={`bg-${borderColor.split('-')[1]}-500/20 text-${borderColor.split('-')[1]}-400 border border-${borderColor.split('-')[1]}-500/30 px-3 py-1 rounded-full text-xs font-semibold`}>
            {content.status}
          </span>
        </div>
        
        <div className="space-y-2 mb-4">
          <p className="text-sm text-muted"><strong>{t('research.labels.authors')}:</strong> {content.authors}</p>
          <p className="text-sm text-muted"><strong>{t('research.labels.venue')}:</strong> {content.venue}</p>
          <p className="text-sm text-blue-400 font-medium"><strong>{t('research.labels.doi')}:</strong> {paper.citation}</p>
          {content.note && (
            <p className="text-sm text-green-400 font-medium"><strong>{t('research.labels.note')}:</strong> {content.note}</p>
          )}
        </div>

        <div className="space-y-3 mb-4">
          <p className="text-sm"><strong className="text-accent">{t('research.labels.problem')}:</strong> {content.problem}</p>
          <p className="text-sm"><strong className="text-accent">{t('research.labels.approach')}:</strong> {content.approach}</p>
          <p className="text-sm"><strong className="text-accent">{t('research.labels.dataset')}:</strong> {content.dataset}</p>
          <p className="text-sm"><strong className="text-accent">{t('research.labels.result')}:</strong> {content.result}</p>
          <p className="text-sm text-accent font-medium"><strong>{t('research.labels.significance')}:</strong> {content.significance}</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {paper.keywords.map((keyword) => (
            <span key={keyword} className="text-xs bg-tertiary/30 text-muted-light px-2 py-1 rounded-md">
              {keyword}
            </span>
          ))}
        </div>

        {paper.link && (
          <a
            href={paper.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-accent hover:text-accent-light transition-colors font-medium"
          >
            <span>📄</span>
            <span>{paper.category === 'published' ? t('research.labels.viewOnIEEE') : t('research.labels.viewPaper')}</span>
          </a>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-secondary to-primary">
      <div className="section-padding">
        <div className="container-width">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('research.title')}
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-accent to-accent-light mx-auto mb-8"></div>
            <p className="text-xl text-muted-light max-w-3xl mx-auto mb-6">
              {t('research.subtitle')}
            </p>
          </motion.div>

          {/* Research Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          >
            {researchMetrics.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * index, duration: 0.6 }}
                className="card text-center"
              >
                <div className="text-3xl font-bold text-accent mb-2">{item.metric}</div>
                <div className="font-semibold text-white mb-1 text-sm">{item.label}</div>
                <div className="text-xs text-muted">{item.description}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Publications */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-accent mb-12 text-center">
              📚 {t('research.title')}
            </h2>
            
            {/* Published in IEEE Xplore */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="text-3xl">🎓</span>
                {t('research.sections.published')}
              </h3>
              <div className="space-y-6">
                {publishedPapers.map(paper => renderPaper(paper, 'border-blue'))}
              </div>
            </div>

            {/* Independent Research Projects */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="text-3xl">📄</span>
                {t('research.sections.independent')}
              </h3>
              <div className="space-y-6">
                {manuscriptPapers.map(paper => renderPaper(paper, 'border-yellow'))}
              </div>
            </div>

            {/* Published Articles */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-white mb-6">{t('research.sections.articles')}</h3>
              <div className="space-y-6">
                {articlePapers.map(paper => renderPaper(paper, 'border-accent'))}
              </div>
            </div>
          </motion.div>

          {/* Research Areas */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-accent mb-12 text-center">
              🔬 {t('research.researchAreas.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {researchAreas.map((area, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index, duration: 0.6 }}
                  className={`card card-hover bg-gradient-to-br ${area.color}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-4xl">{area.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-accent mb-3">{area.title}</h3>
                      <p className="text-sm text-muted-light mb-4 leading-relaxed">{area.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {area.projects.map((project) => (
                          <span key={project} className="text-xs bg-accent/10 text-accent px-2 py-1 rounded-full border border-accent/20">
                            {project}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="card bg-gradient-to-r from-accent/10 to-accent-light/10 text-center max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-accent mb-4">{t('research.summary.title')}</h3>
            <p className="text-lg text-muted-light leading-relaxed">
              {t('research.summary.description')}
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
